import { ORDERABLE_PRODUCT_KEYS, ORDERABLE_PRODUCTS, type LocationSupplies, type OrderableProduct, type SupplyOrder } from "@allergy123/contracts";
import { Button, Card, Field, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { useState } from "react";
import { useBilling, useLoad } from "../api";
import { errorText, isConflict, isForbidden, isUnavailable, orderErrorText } from "../lib/errors";
import { formatCalendarDate, formatDateTime, orderStatusLabel, paymentStatusLabel } from "../lib/format";
import { useSession } from "../session";

/** What each product is, in the practice's words; the names come from the contract so the picker matches the invoice. */
const PRODUCT_NOTES: Record<OrderableProduct, string> = {
  tray_test_kit_package: "one tray and 20 test kits",
  immunotherapy_mixing_kit: "everything to mix a course of immunotherapy",
};

const KIT_PER_PACKAGE = 20;

/**
 * Supplies (design 2026-10-25, ADR 0008): each location's test-kit balance and reorder settings, ordering, and the
 * order history. Counts and order numbers only; nothing on this page names a patient or an encounter. Reading
 * balances needs `clinical:read`; the order history, placing, cancelling and saving settings need `clinical:manage`.
 */
export function SuppliesPage() {
  const s = useSession();
  const billing = useBilling();
  const editable = s.can("clinical:manage");
  const supplies = useLoad(() => billing.supplies.list(), [billing, s.organizationId]);
  const orders = useLoad(() => billing.orders.list(), [billing, s.organizationId]);
  const locationName = (locationId: string) => s.locations.find((l) => l.locationId === locationId)?.name ?? supplies.data?.locations.find((l) => l.locationId === locationId)?.name ?? "Another location";
  const timezoneOf = (locationId: string) => s.locations.find((l) => l.locationId === locationId)?.timezone ?? s.location?.timezone ?? "America/Denver";

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Supplies</h1>
        <p className="max-w-[68ch] text-ink-secondary">
          {s.organizationName}. Each location's test-kit balance is counted from the opening count you enter, plus {KIT_PER_PACKAGE} kits for every tray package shipped since, minus the tests run since. Orders placed here are confirmed by Allergy 123, who charge the payment method on file and ship with tracking.
          {!editable && " Changing settings and ordering need the manage permission."}
        </p>
      </div>
      <div className="flex flex-col gap-6">
        {supplies.state === "error" ? (
          <Card tone="tinted">
            <h2 className="mb-2 text-h2 font-semibold text-anchor">{isUnavailable(supplies.error) ? "Balances are not available here" : "Couldn't load the balances"}</h2>
            <p className="mb-4 text-ink-secondary">{errorText(supplies.error)}</p>
            <Button variant="secondary" surface="card-tinted" onClick={supplies.reload}>
              Try again
            </Button>
          </Card>
        ) : !supplies.data ? (
          <p className="text-ink-secondary">Loading balances…</p>
        ) : supplies.data.locations.length === 0 ? (
          <Card tone="tinted">
            <p className="text-ink-secondary">Your membership covers no active location, so there is no balance to show.</p>
          </Card>
        ) : (
          supplies.data.locations.map((l) => <LocationCard key={`${l.locationId}-${l.settings?.version ?? 0}`} supplies={l} editable={editable} onSaved={supplies.reload} />)
        )}
        <OrderCard locations={supplies.data?.locations ?? s.locations.map((l) => ({ locationId: l.locationId, name: l.name }))} defaultLocationId={s.location?.locationId} editable={editable} onPlaced={orders.reload} />
        <Card as="section" aria-labelledby="orders">
          <h2 id="orders" className="mb-2 text-h2 font-semibold text-anchor">
            Orders
          </h2>
          <p className="mb-4 max-w-[68ch] text-ink-secondary">Newest first. An order can be cancelled here until Allergy 123 confirms it; after that, contact them.</p>
          {orders.state === "error" ? (
            isForbidden(orders.error) ? (
              <Status kind="info" label="The order history needs the manage permission." />
            ) : (
              <Status kind={isUnavailable(orders.error) ? "warning" : "error"} label={errorText(orders.error)} />
            )
          ) : !orders.data ? (
            <p className="text-ink-secondary">Loading orders…</p>
          ) : (
            <OrdersTable orders={orders.data.orders} editable={editable} locationName={locationName} timezoneOf={timezoneOf} onChanged={orders.reload} />
          )}
        </Card>
      </div>
    </>
  );
}

const isCount = (text: string) => /^\d+$/.test(text.trim());

/** One location: the balance, the low-stock badge and a disclosure with the opening count, threshold and auto-reorder. */
function LocationCard({ supplies, editable, onSaved }: { supplies: LocationSupplies; editable: boolean; onSaved: () => void }) {
  const billing = useBilling();
  const settings = supplies.settings;
  const initial = { openingKits: settings ? String(settings.openingKits) : "", openingAsOf: settings?.openingAsOf ?? "", reorderThreshold: String(settings?.reorderThreshold ?? KIT_PER_PACKAGE), autoReorder: settings?.autoReorder ?? false };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const valid = isCount(form.openingKits) && /^\d{4}-\d{2}-\d{2}$/.test(form.openingAsOf) && isCount(form.reorderThreshold);
  const heading = `supplies-${supplies.locationId}`;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setNote(null);
    try {
      await billing.supplies.update(supplies.locationId, { version: settings?.version ?? 0, openingKits: Number(form.openingKits), openingAsOf: form.openingAsOf, reorderThreshold: Number(form.reorderThreshold), autoReorder: form.autoReorder });
      setNote({ kind: "success", text: "Saved. The balance is counted from the new opening count." });
      onSaved();
    } catch (err) {
      setNote({ kind: "error", text: errorText(err) });
      // A stale version means someone saved first: the reload brings their settings into the form (the message says so).
      if (isConflict(err)) onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" aria-labelledby={heading}>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={heading} className="text-h2 font-semibold text-anchor">
          {supplies.name}
        </h2>
        {settings && <span className="text-small text-ink-secondary">settings version {settings.version}</span>}
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
        <div>
          {supplies.balance === null || !settings ? (
            <>
              <p className="text-h2 font-semibold text-ink-secondary">Not set up</p>
              <p className="mt-2 max-w-[40ch] text-ink-secondary">The balance needs an opening count: how many test kits this location had on a given date. Enter it under Settings{editable ? "" : " (a member with the manage permission can)"}.</p>
            </>
          ) : (
            <>
              <p className="text-display font-semibold tabular-nums text-anchor" aria-describedby={`${heading}-unit`}>
                {supplies.balance}
              </p>
              <p id={`${heading}-unit`} className="text-small text-ink-secondary">
                test kits on hand, counted from {formatCalendarDate(settings.openingAsOf)}
              </p>
              {supplies.lowStock && <Status kind="warning" label={`Low stock: at or below the reorder threshold of ${settings.reorderThreshold}`} className="mt-3" />}
            </>
          )}
        </div>
        <details className="rounded-md border border-rule-subtle p-4" open={!settings || undefined}>
          <summary className="cursor-pointer text-body font-medium text-anchor">Settings</summary>
          <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={(e) => void save(e)}>
            <Field label="Opening test kits" type="number" inputMode="numeric" min={0} step={1} value={form.openingKits} onChange={(e) => setForm({ ...form, openingKits: e.target.value })} disabled={!editable} required hint="The kits on the shelf on the date below." />
            <Field label="Counted on" type="date" value={form.openingAsOf} onChange={(e) => setForm({ ...form, openingAsOf: e.target.value })} disabled={!editable} required hint="Tests run and packages shipped from this date change the balance." />
            <Field label="Reorder threshold" type="number" inputMode="numeric" min={0} step={1} value={form.reorderThreshold} onChange={(e) => setForm({ ...form, reorderThreshold: e.target.value })} disabled={!editable} required hint="At or below this many kits the location shows low stock." />
            <label className="flex items-start gap-2 self-end pb-2 text-body">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-[var(--cta-bg)]" checked={form.autoReorder} onChange={(e) => setForm({ ...form, autoReorder: e.target.checked })} disabled={!editable} />
              <span>
                Reorder automatically
                <span className="block text-small text-ink-secondary">When the balance reaches the threshold, a tray package order is placed overnight; Allergy 123 staff confirm and charge it like any other order.</span>
              </span>
            </label>
            {note && <Status kind={note.kind} label={note.text} className="sm:col-span-2" />}
            {editable && (
              <div className="sm:col-span-2">
                <Button type="submit" disabled={busy || !dirty || !valid}>
                  {settings ? "Save settings" : "Set up the balance"}
                </Button>
              </div>
            )}
          </form>
        </details>
      </div>
    </Card>
  );
}

/** Place an order: product, quantity 1 to 10, location. The core API has no idempotency ledger here, so the result is shown once and never retried on its own. */
function OrderCard({ locations, defaultLocationId, editable, onPlaced }: { locations: { locationId: string; name: string }[]; defaultLocationId?: string; editable: boolean; onPlaced: () => void }) {
  const billing = useBilling();
  const [product, setProduct] = useState<OrderableProduct>("tray_test_kit_package");
  const [quantity, setQuantity] = useState("1");
  const [locationId, setLocationId] = useState(defaultLocationId ?? locations[0]?.locationId ?? "");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const qty = Number(quantity);
  const valid = isCount(quantity) && qty >= 1 && qty <= 10 && locationId !== "";
  const chosen = locationId || (locations[0]?.locationId ?? "");

  async function place(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setNote(null);
    try {
      const { order } = await billing.orders.place({ locationId: chosen, sku: product, quantity: qty });
      setNote({ kind: "success", text: `Order ${order.orderNumber} placed: ${order.lines.map((l) => `${l.quantity} × ${l.name}`).join(", ")}. Allergy 123 confirms it and charges the payment method on file.` });
      setQuantity("1");
      onPlaced();
    } catch (err) {
      setNote({ kind: "error", text: errorText(err) });
      // The order may or may not exist after a failure that was not a refusal; the list below shows it, and a duplicate can be cancelled while received.
      onPlaced();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" aria-labelledby="order-supplies">
      <h2 id="order-supplies" className="mb-2 text-h2 font-semibold text-anchor">
        Order supplies
      </h2>
      <p className="mb-4 max-w-[68ch] text-ink-secondary">Prices follow your plan; the order is charged to the payment method on file once Allergy 123 confirms it.{!editable && " Placing an order needs the manage permission."}</p>
      <form className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" onSubmit={(e) => void place(e)}>
        <Select label="Product" options={ORDERABLE_PRODUCT_KEYS.map((k) => ({ value: k, label: ORDERABLE_PRODUCTS[k].name }))} value={product} onChange={(e) => setProduct(e.target.value as OrderableProduct)} disabled={!editable} hint={`${ORDERABLE_PRODUCTS[product].name}: ${PRODUCT_NOTES[product]}.`} />
        <Field label="Quantity (1 to 10)" type="number" inputMode="numeric" min={1} max={10} step={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} disabled={!editable} required />
        <Select label="Deliver to" options={locations.map((l) => ({ value: l.locationId, label: l.name }))} value={chosen} onChange={(e) => setLocationId(e.target.value)} disabled={!editable || locations.length === 0} />
        {note && <Status kind={note.kind} label={note.text} className="sm:col-span-2 lg:col-span-3" />}
        {editable && (
          <div className="sm:col-span-2 lg:col-span-3">
            <Button type="submit" disabled={busy || !valid}>
              {busy ? "Placing…" : "Place order"}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

function OrdersTable({ orders, editable, locationName, timezoneOf, onChanged }: { orders: SupplyOrder[]; editable: boolean; locationName: (id: string) => string; timezoneOf: (id: string) => string; onChanged: () => void }) {
  const billing = useBilling();
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function cancel(order: SupplyOrder) {
    if (!window.confirm(`Cancel order ${order.orderNumber}? This cannot be undone.`)) return;
    setCancelling(order.id);
    setError(null);
    try {
      await billing.orders.cancel(order.id);
    } catch (err) {
      setError(orderErrorText(err));
    } finally {
      setCancelling(null);
      onChanged();
    }
  }

  return (
    <>
      {error && <Status kind="error" label={error} className="mb-4 block" />}
      <Table caption={`${orders.length} order${orders.length === 1 ? "" : "s"}`}>
        <thead>
          <tr>
            <Th>Order</Th>
            <Th>Product</Th>
            <Th>Location</Th>
            <Th>Placed</Th>
            <Th>Status</Th>
            <Th>Payment</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && (
            <tr>
              <Td colSpan={7} className="text-ink-secondary">
                No orders yet.
              </Td>
            </tr>
          )}
          {orders.map((o) => (
            <tr key={o.id}>
              <Td className="whitespace-nowrap tabular-nums">{o.orderNumber}</Td>
              <Td>
                {o.lines.map((l) => (
                  <span key={l.sku} className="block">
                    {l.quantity} × {l.name}
                  </span>
                ))}
                {o.source === "auto_reorder" && <span className="block text-small text-ink-secondary">Automatic reorder</span>}
              </Td>
              <Td>{locationName(o.locationId)}</Td>
              <Td className="whitespace-nowrap">{formatDateTime(o.createdAt, timezoneOf(o.locationId))}</Td>
              <Td>
                {orderStatusLabel(o.status)}
                {o.status === "shipped" && (o.carrier || o.trackingNumber) && (
                  <span className="block text-small text-ink-secondary">
                    {[o.carrier, o.trackingNumber].filter(Boolean).join(" · ")}
                    {o.shippedAt && ` · ${formatDateTime(o.shippedAt, timezoneOf(o.locationId))}`}
                  </span>
                )}
                {o.status === "confirmed" && o.confirmedAt && <span className="block text-small text-ink-secondary">{formatDateTime(o.confirmedAt, timezoneOf(o.locationId))}</span>}
              </Td>
              <Td>{paymentStatusLabel(o.paymentStatus)}</Td>
              <Td>
                {editable && o.status === "received" ? (
                  <Button variant="secondary" surface="card" disabled={cancelling !== null} onClick={() => void cancel(o)}>
                    {cancelling === o.id ? "Cancelling…" : "Cancel"}
                  </Button>
                ) : (
                  <span className="text-small text-ink-secondary">—</span>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}
